import { FormEvent, useCallback, useEffect, useId, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, type GenerationJob, runtimeUrlFor, ApiError } from '../lib/api';
import { useSession } from '../context/SessionContext';
import { setPendingAction } from '../lib/pendingActions';
import { GuestGate } from '../components/GuestGate';
import {
  ViewerCanvas,
  type LightingPreset,
  type InspectMode,
  type PresentationSettings,
} from '../components/ViewerCanvas';

type ToolId = 'agent' | 'image' | 'model' | 'materials' | 'animate' | 'scene' | 'print';
type InputTab = 'image' | 'text' | 'import' | 'multi';

const TOOLS: Array<{ id: ToolId; label: string }> = [
  { id: 'agent', label: 'Agent' },
  { id: 'image', label: 'Image' },
  { id: 'model', label: 'Model' },
  { id: 'materials', label: 'Materials' },
  { id: 'animate', label: 'Animate' },
  { id: 'scene', label: 'Scene' },
  { id: 'print', label: 'Print' },
];

const LIGHTING: LightingPreset[] = ['studio', 'daylight', 'gallery', 'evening', 'industrial'];
const RECIPES = ['Decor', 'Exhibition Object', 'Collectible', 'Character', 'Product Visualization', 'Print Object'];

async function fetchRuntimeBlob(assetId: string): Promise<string> {
  const res = await fetch(runtimeUrlFor(assetId), {
    credentials: 'include',
    headers: { Accept: 'model/gltf-binary,*/*' },
  });
  if (!res.ok) throw new ApiError(res.status, 'Runtime unavailable', 'RUNTIME_FORBIDDEN');
  const buf = await res.arrayBuffer();
  const blobUrl = URL.createObjectURL(new Blob([buf], { type: 'model/gltf-binary' }));
  return `${blobUrl}#runtime.glb`;
}

function newIdempotencyKey() {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function WorkspacePage() {
  const { authenticated, startKeyra } = useSession();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const formId = useId();
  const initialAsset = params.get('asset') ?? '';
  const initialPrompt = params.get('prompt') ?? '';
  const initialTab = (params.get('tab') as InputTab | null) ?? null;

  const [tool, setTool] = useState<ToolId>('model');
  const [inputTab, setInputTab] = useState<InputTab>(initialTab ?? 'text');
  const [gate, setGate] = useState(false);
  const [prompt, setPrompt] = useState(initialPrompt);
  const [job, setJob] = useState<GenerationJob | null>(null);
  const [submissionId, setSubmissionId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [assetId, setAssetId] = useState(initialAsset);
  const [assetTitle, setAssetTitle] = useState('Untitled object');
  const [runtimeUrl, setRuntimeUrl] = useState<string | null>(null);
  const [viewerStatus, setViewerStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [file, setFile] = useState<File | null>(null);
  const [multiFiles, setMultiFiles] = useState<File[]>([]);
  const [primaryView, setPrimaryView] = useState(0);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [quality, setQuality] = useState<'draft' | 'standard'>('standard');
  const [agentRequest, setAgentRequest] = useState('');
  const [agentBrief, setAgentBrief] = useState<{
    proposedPrompt: string;
    suggestions: string[];
    proposedSequence: Array<{ step: number; action: string; costCents: number }>;
    disclaimer: string;
  } | null>(null);
  const [sceneNote, setSceneNote] = useState('');
  const [versions, setVersions] = useState<Array<{ id: string; versionNumber: number }>>([]);
  const [presentation, setPresentation] = useState<PresentationSettings>({
    lightingPreset: 'studio',
    environmentIntensity: 1,
    exposure: 0,
    inspectMode: 'shaded',
    showGrid: false,
  });
  const [capabilities, setCapabilities] = useState<
    Array<{ id: string; operation: string; availability: string; availabilityReason?: string; provider?: string }>
  >([]);

  useEffect(() => {
    void api.getStudioCapabilities().then((r) => setCapabilities(r.capabilities)).catch(() => undefined);
  }, []);

  const loadAsset = useCallback(
    async (id: string) => {
      setViewerStatus('loading');
      setError(null);
      try {
        const { asset } = await api.getAsset(id);
        setAssetTitle(asset.title);
        setAssetId(id);
        const url = await fetchRuntimeBlob(id);
        setRuntimeUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev.split('#')[0]!);
          return url;
        });
        setViewerStatus('ready');
        if (authenticated) {
          const [{ presentation: saved }, ver] = await Promise.all([
            api.getCurrentPresentation(id),
            api.listVersions(id).catch(() => ({ versions: [] })),
          ]);
          if (saved?.settings) {
            setPresentation((p) => ({ ...p, ...(saved.settings as PresentationSettings) }));
          }
          setVersions(ver.versions ?? []);
        }
      } catch (e) {
        setViewerStatus('error');
        setError(e instanceof Error ? e.message : 'Failed to load asset');
      }
    },
    [authenticated],
  );

  useEffect(() => {
    if (initialAsset) void loadAsset(initialAsset);
  }, [initialAsset, loadAsset]);

  useEffect(() => {
    if (!job || job.state === 'READY' || job.state === 'FAILED' || job.state === 'CANCELED') return;
    const t = setInterval(() => {
      void api.getJob(job.id).then((r) => {
        setJob(r.job);
        if (r.job.state === 'READY' && r.job.resultAssetId) void loadAsset(r.job.resultAssetId);
      });
    }, 1500);
    return () => clearInterval(t);
  }, [job, loadAsset]);

  function requireAuth(path = '/workspace') {
    if (!authenticated) {
      setPendingAction({ type: 'create' });
      setGate(true);
      void startKeyra(path);
      return false;
    }
    return true;
  }

  function cap(id: string) {
    return capabilities.find((c) => c.id === id);
  }

  async function onGenerate(e: FormEvent) {
    e.preventDefault();
    if (!requireAuth()) return;
    setBusy(true);
    setError(null);
    setTool('model');
    try {
      const quote = await api.quoteStudioJob({
        capabilityId: 'local-text-procedural',
        parameters: { prompt, quality },
      });
      const result = await api.createStudioJob({
        capabilityId: 'local-text-procedural',
        quoteId: quote.quoteId,
        idempotencyKey: newIdempotencyKey(),
        prompt,
        title: prompt.slice(0, 80) || 'Generated object',
        parameters: { quality },
      });
      setSubmissionId(result.submission.id);
      if (result.job) setJob(result.job);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Generation failed');
    } finally {
      setBusy(false);
    }
  }

  async function onImport(e: FormEvent) {
    e.preventDefault();
    if (!requireAuth('/workspace') || !file) return;
    setBusy(true);
    setError(null);
    try {
      const init = await api.initUpload({
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
        byteSize: file.size,
      });
      await api.uploadBytes(init.uploadId, file);
      const done = await api.completeUpload(init.uploadId);
      setAssetId(done.assetId);
      setAssetTitle(file.name.replace(/\.[^.]+$/, ''));
      try {
        await loadAsset(done.assetId);
      } catch {
        setViewerStatus('idle');
        setError('Upload queued for conversion. Open My Library when processing finishes.');
      }
      navigate(`/workspace?asset=${done.assetId}`, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setBusy(false);
    }
  }

  async function onMultiViewAttempt(e: FormEvent) {
    e.preventDefault();
    if (!requireAuth()) return;
    if (multiFiles.length < 1) {
      setError('Add 1–4 photographs of the same object.');
      return;
    }
    if (multiFiles.length > 4) {
      setError('Limit is 1–4 views. Excess images were not discarded — remove some first.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.quoteStudioJob({ capabilityId: 'meshy-multi-image' });
      setError('Unexpected: multi-image quote should be blocked until configured.');
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Multi-view generation is not configured. One object, 1–4 views — not batch of unrelated images.',
      );
    } finally {
      setBusy(false);
    }
  }

  async function onMeshyOp(capabilityId: string) {
    if (!requireAuth()) return;
    setBusy(true);
    setError(null);
    try {
      await api.quoteStudioJob({ capabilityId });
      setError('Unexpected: Meshy quote should be blocked.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Provider not configured');
    } finally {
      setBusy(false);
    }
  }

  async function onSavePresentation() {
    if (!assetId || !requireAuth(`/workspace?asset=${assetId}`)) return;
    setSaveState('saving');
    try {
      await api.savePresentation(assetId, {
        label: 'Workspace presentation',
        settings: presentation,
        makeCurrent: true,
      });
      setSaveState('saved');
    } catch (err) {
      setSaveState('error');
      setError(err instanceof Error ? err.message : 'Save failed');
    }
  }

  async function onAgentBrief(e: FormEvent) {
    e.preventDefault();
    if (!requireAuth()) return;
    setBusy(true);
    setError(null);
    try {
      const { brief } = await api.agentBrief(agentRequest);
      setAgentBrief(brief);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Assistant failed');
    } finally {
      setBusy(false);
    }
  }

  async function onSaveScene() {
    if (!requireAuth()) return;
    setBusy(true);
    try {
      const created = await api.createProject({
        title: sceneNote.trim() || 'Gallery scene',
      });
      setError(null);
      navigate(`/studio/${created.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Scene save failed');
    } finally {
      setBusy(false);
    }
  }

  const meshyImage = cap('meshy-image-to-3d');

  return (
    <div className="workspace">
      <header className="workspace__top">
        <div className="workspace__crumbs">
          <Link to="/">Explore</Link>
          <span aria-hidden>/</span>
          <span>Create</span>
          <span aria-hidden>/</span>
          <strong>{assetTitle}</strong>
        </div>
        <div className="row">
          <span className="muted" style={{ fontSize: '0.875rem' }}>
            {saveState === 'saving'
              ? 'Saving…'
              : saveState === 'saved'
                ? 'Saved'
                : saveState === 'error'
                  ? 'Save failed'
                  : 'Unsaved local edits'}
          </span>
          {job ? (
            <span className="muted" style={{ fontSize: '0.875rem' }} role="status">
              Job: {job.state}
            </span>
          ) : null}
          <button type="button" className="btn" disabled={!assetId || busy} onClick={() => void onSavePresentation()}>
            Save presentation
          </button>
          {assetId ? (
            <Link className="btn primary" to={`/library?focus=${assetId}`}>
              Open in Library
            </Link>
          ) : null}
        </div>
      </header>

      <div className="workspace__body">
        <nav className="workspace__rail" aria-label="Creator tools">
          {TOOLS.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`workspace__rail-btn ${tool === t.id ? 'active' : ''}`}
              aria-pressed={tool === t.id}
              onClick={() => {
                setTool(t.id);
                setError(null);
              }}
            >
              {t.label}
            </button>
          ))}
        </nav>

        <aside className="workspace__context" aria-label="Inputs and settings">
          {tool === 'agent' && (
            <form onSubmit={onAgentBrief}>
              <label className="field" htmlFor={`${formId}-agent`}>
                3DSpin Assistant
                <textarea
                  id={`${formId}-agent`}
                  className="prompt"
                  style={{ minHeight: 100 }}
                  value={agentRequest}
                  onChange={(e) => setAgentRequest(e.target.value)}
                  placeholder="Create a brushed-metal display stand, three finish options…"
                  required
                />
              </label>
              <p className="muted" style={{ fontSize: '0.8125rem' }}>
                Proposes an editable brief and sequence. Does not spend or publish without approval.
              </p>
              <button className="btn primary" type="submit" disabled={busy} style={{ width: '100%' }}>
                Propose brief
              </button>
              {agentBrief && (
                <div className="panel" style={{ marginTop: '0.75rem', padding: '0.75rem' }}>
                  <p style={{ margin: 0, fontSize: '0.875rem' }}>{agentBrief.proposedPrompt}</p>
                  <ol style={{ fontSize: '0.8125rem', paddingLeft: '1.1rem' }}>
                    {agentBrief.proposedSequence.map((s) => (
                      <li key={s.step}>
                        {s.action} ({s.costCents === 0 ? 'free' : `~${s.costCents}¢`})
                      </li>
                    ))}
                  </ol>
                  <button
                    type="button"
                    className="btn"
                    onClick={() => {
                      setPrompt(agentBrief.proposedPrompt);
                      setInputTab('text');
                      setTool('model');
                    }}
                  >
                    Use as text prompt
                  </button>
                  <p className="muted" style={{ fontSize: '0.75rem', marginBottom: 0 }}>
                    {agentBrief.disclaimer}
                  </p>
                </div>
              )}
            </form>
          )}

          {(tool === 'image' || tool === 'model') && (
            <>
              <div className="workspace__tabs" role="tablist" aria-label="Input method">
                {(['image', 'text', 'multi', 'import'] as InputTab[]).map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    role="tab"
                    aria-selected={inputTab === tab}
                    className={inputTab === tab ? 'active' : ''}
                    onClick={() => setInputTab(tab)}
                  >
                    {tab === 'multi' ? 'Multi' : tab === 'image' ? 'Image' : tab === 'text' ? 'Text' : 'Import'}
                  </button>
                ))}
              </div>

              {inputTab === 'text' && (
                <form onSubmit={onGenerate}>
                  <label className="field">
                    Recipe
                    <select
                      onChange={(e) => {
                        if (e.target.value) setPrompt((p) => p || `${e.target.value}: `);
                      }}
                      defaultValue=""
                    >
                      <option value="">Editable starter…</option>
                      {RECIPES.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="field">
                    Describe an object
                    <textarea
                      className="prompt"
                      style={{ minHeight: 120 }}
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      placeholder="Brushed-metal display stand for a museum gallery…"
                      required
                    />
                  </label>
                  <label className="field">
                    Output quality
                    <select value={quality} onChange={(e) => setQuality(e.target.value as 'draft' | 'standard')}>
                      <option value="draft">Draft preview</option>
                      <option value="standard">Standard</option>
                    </select>
                  </label>
                  <button
                    type="button"
                    className="btn ghost"
                    style={{ marginBottom: '0.5rem' }}
                    onClick={() => setShowAdvanced((v) => !v)}
                  >
                    {showAdvanced ? 'Hide advanced' : 'Advanced'}
                  </button>
                  {showAdvanced && (
                    <p className="muted" style={{ fontSize: '0.8125rem' }}>
                      Topology / face-count / Meshy model IDs appear here only when the selected capability supports them.
                      Geometry resolution ≠ texture resolution.
                    </p>
                  )}
                  <p className="muted" style={{ fontSize: '0.8125rem' }}>
                    Cost: local procedural $0. Meshy text preview/refine stays gated until contract-tested.
                  </p>
                  <button className="btn primary" type="submit" disabled={busy} style={{ width: '100%' }}>
                    {busy ? 'Working…' : 'Generate'}
                  </button>
                </form>
              )}

              {inputTab === 'import' && (
                <form onSubmit={onImport}>
                  <label className="field">
                    Import a model
                    <input
                      type="file"
                      accept=".glb,.gltf,.obj,.stl,.ply"
                      onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                      required
                    />
                  </label>
                  <button className="btn primary" type="submit" disabled={busy || !file} style={{ width: '100%' }}>
                    {busy ? 'Uploading…' : 'Import'}
                  </button>
                </form>
              )}

              {inputTab === 'image' && (
                <div>
                  <label className="field">
                    Image to 3D
                    <input type="file" accept="image/png,image/jpeg,image/webp" disabled aria-disabled="true" />
                  </label>
                  <p className="muted" style={{ fontSize: '0.8125rem' }}>
                    {meshyImage?.availabilityReason ?? 'Not configured (S6).'}
                  </p>
                  <button
                    className="btn primary"
                    type="button"
                    disabled={busy}
                    onClick={() => void onMeshyOp('meshy-image-to-3d')}
                  >
                    Generate (will refuse if unconfigured)
                  </button>
                </div>
              )}

              {inputTab === 'multi' && (
                <form onSubmit={onMultiViewAttempt}>
                  <label className="field">
                    Multi-view (same object, 1–4)
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      multiple
                      onChange={(e) => {
                        const list = Array.from(e.target.files ?? []);
                        setMultiFiles(list.slice(0, 8));
                        setPrimaryView(0);
                      }}
                    />
                  </label>
                  {multiFiles.length > 0 && (
                    <ul style={{ fontSize: '0.8125rem', paddingLeft: '1.1rem' }}>
                      {multiFiles.map((f, i) => (
                        <li key={`${f.name}-${i}`}>
                          <label>
                            <input
                              type="radio"
                              name="primary-view"
                              checked={primaryView === i}
                              onChange={() => setPrimaryView(i)}
                            />{' '}
                            {f.name}
                            {i === primaryView ? ' (front)' : ''}
                            {i >= 4 ? ' — excess, remove before submit' : ''}
                          </label>
                        </li>
                      ))}
                    </ul>
                  )}
                  <p className="muted" style={{ fontSize: '0.8125rem' }}>
                    Not batch generation of unrelated objects. Excess views are never silently discarded.
                  </p>
                  <button className="btn primary" type="submit" disabled={busy} style={{ width: '100%' }}>
                    Quote multi-view job
                  </button>
                </form>
              )}
            </>
          )}

          {tool === 'materials' && (
            <div className="workspace__materials">
              <p className="muted" style={{ fontSize: '0.8125rem', marginTop: 0 }}>
                Local viewer edits only — no vendor charge (T16).
              </p>
              <label className="field">
                Lighting
                <select
                  value={presentation.lightingPreset ?? 'studio'}
                  onChange={(e) =>
                    setPresentation((p) => ({ ...p, lightingPreset: e.target.value as LightingPreset }))
                  }
                >
                  {LIGHTING.map((l) => (
                    <option key={l} value={l}>
                      {l}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                Inspect
                <select
                  value={presentation.inspectMode ?? 'shaded'}
                  onChange={(e) =>
                    setPresentation((p) => ({ ...p, inspectMode: e.target.value as InspectMode }))
                  }
                >
                  <option value="shaded">Shaded</option>
                  <option value="clay">Clay</option>
                  <option value="wireframe">Wireframe</option>
                </select>
              </label>
              <label className="field">
                Roughness
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.01}
                  value={presentation.roughness ?? 0.5}
                  onChange={(e) => setPresentation((p) => ({ ...p, roughness: Number(e.target.value) }))}
                />
              </label>
              <label className="field">
                Metalness
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.01}
                  value={presentation.metalness ?? 0}
                  onChange={(e) => setPresentation((p) => ({ ...p, metalness: Number(e.target.value) }))}
                />
              </label>
              <button type="button" className="btn primary" disabled={!assetId} onClick={() => void onSavePresentation()}>
                Apply &amp; save
              </button>
              <hr style={{ border: 0, borderTop: '1px solid var(--line)', margin: '1rem 0' }} />
              <p className="muted" style={{ fontSize: '0.8125rem' }}>
                AI retexture / remesh are separate paid ops — not these sliders.
              </p>
              <button type="button" className="btn" disabled={busy} onClick={() => void onMeshyOp('meshy-retexture')}>
                Quote AI retexture
              </button>
              <button type="button" className="btn" disabled={busy} onClick={() => void onMeshyOp('meshy-remesh')}>
                Quote remesh
              </button>
            </div>
          )}

          {tool === 'animate' && (
            <div>
              <p style={{ marginTop: 0, fontWeight: 600 }}>Character / motion</p>
              <p className="muted" style={{ fontSize: '0.875rem' }}>
                Rigging is limited to suitable textured humanoids per programmatic docs (S10). Quadrupeds and arbitrary
                objects stay disabled. Motions require a tested library contract (S11).
              </p>
              <button type="button" className="btn" disabled={busy} onClick={() => void onMeshyOp('meshy-rig')}>
                Check Auto-rig eligibility
              </button>
            </div>
          )}

          {tool === 'scene' && (
            <div>
              <p style={{ marginTop: 0, fontWeight: 600 }}>Scene staging</p>
              <p className="muted" style={{ fontSize: '0.875rem' }}>
                Lightweight composition — not a Sansar world engine. Saves a versioned project with asset dependencies.
              </p>
              <label className="field">
                Scene title
                <input value={sceneNote} onChange={(e) => setSceneNote(e.target.value)} placeholder="Gallery display" />
              </label>
              <label className="field">
                Room template
                <select defaultValue="gallery">
                  <option value="gallery">Gallery</option>
                  <option value="music">Music room</option>
                  <option value="product">Product display</option>
                </select>
              </label>
              <button type="button" className="btn primary" disabled={busy} onClick={() => void onSaveScene()}>
                Save scene project
              </button>
            </div>
          )}

          {tool === 'print' && (
            <div>
              <p style={{ marginTop: 0, fontWeight: 600 }}>Print workspace</p>
              <p className="muted" style={{ fontSize: '0.875rem' }}>
                Gated until printability analysis works under our account (S15). A validation pass is not a manufacturing
                warranty. STL ≠ colored 3MF.
              </p>
              <button
                type="button"
                className="btn"
                disabled={busy}
                onClick={() => void onMeshyOp('meshy-printability')}
              >
                Analyze printability
              </button>
            </div>
          )}
        </aside>

        <section className="workspace__stage" aria-label="Model stage">
          <ViewerCanvas
            runtimeUrl={runtimeUrl}
            title={assetTitle}
            status={viewerStatus}
            error={error && viewerStatus === 'error' ? error : null}
            presentation={presentation}
            darkCanvas
          />
          {error && viewerStatus !== 'error' ? <p className="err workspace__stage-msg">{error}</p> : null}
        </section>

        <aside className="workspace__inspector" aria-label="Details and history">
          <h2 style={{ margin: '0 0 0.5rem', fontSize: '0.9375rem' }}>Details</h2>
          {assetId ? (
            <dl className="workspace__meta">
              <dt>Asset</dt>
              <dd style={{ wordBreak: 'break-all' }}>{assetId}</dd>
              <dt>Title</dt>
              <dd>{assetTitle}</dd>
              <dt>Rights</dt>
              <dd>Private until publish</dd>
            </dl>
          ) : (
            <p className="muted" style={{ fontSize: '0.875rem' }}>
              Upload an image, describe an object, or import a model.
            </p>
          )}
          <h2 style={{ margin: '1rem 0 0.5rem', fontSize: '0.9375rem' }}>Versions</h2>
          {versions.length ? (
            <ul className="workspace__caps">
              {versions.map((v) => (
                <li key={v.id}>v{v.versionNumber}</li>
              ))}
            </ul>
          ) : (
            <p className="muted" style={{ fontSize: '0.875rem' }}>None yet</p>
          )}
          <h2 style={{ margin: '1rem 0 0.5rem', fontSize: '0.9375rem' }}>Job</h2>
          <p className="muted" style={{ fontSize: '0.875rem' }}>
            {job ? job.state : 'None'}
            {submissionId ? ` · ${submissionId.slice(0, 8)}…` : ''}
          </p>
          {submissionId && job && !['READY', 'FAILED', 'CANCELED'].includes(job.state) ? (
            <button
              type="button"
              className="btn"
              onClick={() =>
                void api.cancelStudioJob(submissionId).then((r) => setError(r.reason)).catch(() => undefined)
              }
            >
              Request cancel
            </button>
          ) : null}
          <h2 style={{ margin: '1rem 0 0.5rem', fontSize: '0.9375rem' }}>Capabilities</h2>
          <ul className="workspace__caps">
            {capabilities.slice(0, 6).map((c) => (
              <li key={c.id}>
                <strong>{c.operation}</strong>: {c.availability}
              </li>
            ))}
          </ul>
        </aside>
      </div>

      <GuestGate open={gate && !authenticated} onDismiss={() => navigate('/')} />
    </div>
  );
}
